import { split, ApolloLink } from '@apollo/client/link'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { BatchHttpLink } from '@apollo/client/link/batch-http'
import { getMainDefinition } from '@apollo/client/utilities'
import { createClient } from 'graphql-ws'
import { ApolloClient, InMemoryCache, HttpLink } from '@apollo/client'

type CreateApolloClientProps = {
  uri: string
  credentials?: RequestCredentials
  getToken?: () => string | null
  sendBatchHeader?: boolean
  // Operations that must not be coalesced into a batch. A batched response is a
  // single JSON array emitted once the slowest operation resolves — Apollo Server
  // has no incremental delivery — so anything sharing a batch with a slow query
  // waits for it. Name the operations whose result should land independently.
  unbatchedOperations?: string[]
  // Links that run after auth and before the HTTP transport, so an app can answer
  // some operations itself and forward the rest.
  requestLinks?: ApolloLink[]
}

// Centralized cache construction. Entities that carry an `id` (Cart, CartItem,
// Tag, Project, SampledItem, SimilarProduct, PairsWithProduct, CurrentUser, …)
// are already normalized by Apollo's default `__typename` + `id` keying. The
// PDP/search types (ProductByVariantSkusResult, SearchedProduct, …) are
// locale-dependent, and `locale` is a query argument rather than a field — so it
// can't participate in `keyFields`. Normalizing them by `sku` would collide
// across locales, so they're intentionally left as arg-scoped query results,
// which already dedupe same-variable revisits.
//
// VariantAvailability is the exception: it carries no locale-dependent fields,
// so keying it by `sku` is safe and lets a refetch patch every consumer holding
// that variant rather than only the query that issued it.
function buildInMemoryCache(): InMemoryCache {
  return new InMemoryCache({
    typePolicies: {
      VariantAvailability: {
        keyFields: ['sku'],
      },
    },
  })
}

export function createApolloClient({
  uri,
  credentials = 'include',
  getToken,
  sendBatchHeader = false,
  unbatchedOperations = [],
  requestLinks = [],
}: CreateApolloClientProps) {
  // Coalesce operations fired in the same ~10ms window (e.g. the burst of
  // queries on PDP load, which fire in one synchronous tick) into a single HTTP
  // request, while keeping the added latency on lone operations small. The BFF
  // opts in via `allowBatchedHttpRequests` in apps/mtag/src/api/server.ts and
  // caps the batch size there.
  const batchLink = new BatchHttpLink({
    uri,
    credentials,
    batchMax: 10,
    batchInterval: 10,
    ...(sendBatchHeader ? { headers: { 'X-Batch': 'true' } } : {}),
  })

  const unbatched = new Set(unbatchedOperations)

  const httpLink = unbatched.size
    ? split(
        // Anonymous operations have no name; they stay on the batched path.
        (operation) => unbatched.has(operation.operationName ?? ''),
        new HttpLink({ uri, credentials }),
        batchLink,
      )
    : batchLink

  const authLink = new ApolloLink((operation, forward) => {
    const token = getToken?.()
    if (token) {
      operation.setContext(
        ({ headers = {} }: { headers?: Record<string, string> }) => ({
          headers: { ...headers, Authorization: `Bearer ${token}` },
        }),
      )
    }
    return forward(operation)
  })

  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: ApolloLink.from([authLink, ...requestLinks, httpLink]),
    cache: buildInMemoryCache(),
  })
}

type CreateSubscriptionApolloClientProps = {
  httpUri: string
  wsUri?: string
  credentials?: RequestCredentials
  subscriptionLink?: ApolloLink
  getToken?: () => string | null
  cache?: InMemoryCache
}

export function createSubscriptionApolloClient({
  httpUri,
  wsUri,
  credentials = 'include',
  subscriptionLink,
  getToken,
  cache,
}: CreateSubscriptionApolloClientProps) {
  const baseHttpLink = new HttpLink({ uri: httpUri, credentials })

  const authLink = new ApolloLink((operation, forward) => {
    const token = getToken?.()
    if (token) {
      operation.setContext(
        ({ headers = {} }: { headers?: Record<string, string> }) => ({
          headers: { ...headers, Authorization: `Bearer ${token}` },
        }),
      )
    }
    return forward(operation)
  })

  const httpLink = getToken ? authLink.concat(baseHttpLink) : baseHttpLink

  if (!subscriptionLink && !wsUri) {
    return new ApolloClient({
      ssrMode: typeof window === 'undefined',
      link: httpLink,
      cache: cache ?? buildInMemoryCache(),
    })
  }

  const wsOrCustomLink: ApolloLink =
    subscriptionLink ??
    new GraphQLWsLink(
      createClient({
        url: wsUri as string,
        connectionParams: () => {
          const token = getToken?.()

          if (!token) {
            return {}
          }

          return { Authorization: `Bearer ${token}` }
        },
      }),
    )

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query)
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      )
    },
    wsOrCustomLink,
    httpLink,
  )

  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: splitLink,
    cache: cache ?? buildInMemoryCache(),
  })
}
